import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Tooltip } from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import {
  validateBasics, validatePricing, validateFulfillmentShape,
  validateFulfillmentAgainstPricing, sumLineItems, hasErrors,
} from "./validators";
import { createProduct, updateProduct, addPricingVersion, getProduct, apiErrorMessage } from "./checkoutProductsApi";
import { buildStepIssues, issueSummary, issueCount, joinStepNames, STEP_NAMES } from "./stepIssues";
import { SYM } from "./tokens";
import {
  keyFor, findByKey, variantsAtDuration, familyLabel, flowPacksAt, flowAddOnsFor, catalogPriceOfKey,
} from "./checkoutCatalog";
import {
  TYPE_PILLS, VISIBLE_TYPE_PILLS, CREDIT_LINE,
  creditFieldsFor, entitlementsFor, billingLabelFor,
} from "./studioConstants";
import { emptyForm, formFromProduct, migrateFulfillment, editSnapshot } from "./productForm";
import { buildLines, toLineItems } from "./pricingLines";
import useStyles from "./studioStyles";
import { StepSection } from "./StepSection";
import IdentityStep from "./IdentityStep";
import EntitlementsStep from "./EntitlementsStep";
import PricingStep from "./PricingStep";
import BuyerPreview from "./BuyerPreview";

/**
 * Product Studio — full-page create/edit for a checkout product. Left: a 3-step
 * builder (Identity → Entitlements → Pricing). Right: a live buyer preview.
 * `product` prop absent = create; present = edit (prefilled, steps unlocked,
 * pricing edited as a new version). basePrice = sum of the pricing line items.
 */
const ProductStudio = ({ product = null, catalog = { families: [] }, onClose, onSaved, showSnackbar }) => {
  const classes = useStyles();
  // Local copy so edit mode can refresh it in place (e.g. after publishing a new
  // pricing version) without navigating back to the list.
  const [prod, setProd] = useState(product);
  const isEdit = !!prod;
  const [form, setForm] = useState(() => (prod ? formFromProduct(prod) : emptyForm()));
  const [featInput, setFeatInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [audienceLabels, setAudienceLabels] = useState({});
  // The server rejects duplicate names; shown under the field rather than in a
  // snackbar that vanishes before you can act on it.
  const [nameError, setNameError] = useState("");
  // Edit only: whether the pricing step is drafting a new pricing version.
  const [pricingDraft, setPricingDraft] = useState(false);
  // Step gating is create-only: a step stays locked until the previous is done,
  // and errors show only once a step is attempted. Edit unlocks all three and
  // shows errors up front, since every value is already prefilled.
  const [activeStep, setActiveStep] = useState(1);
  const [maxStep, setMaxStep] = useState(isEdit ? 3 : 1);
  const [attempted, setAttempted] = useState(isEdit ? { 1: true, 2: true, 3: true } : { 1: false, 2: false, 3: false });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const sub = form.fulfillment?.subscription || null;
  const credits = form.fulfillment?.credits || null;
  const webinar = form.fulfillment?.webinar || null;
  // Credit fields this product type may sell (ads → Meta Ads only).
  const CF = creditFieldsFor(form.type);
  // Single-entitlement types (webinar, ads) auto-enable their one card and hide
  // its toggle. Reads the gated surface, so a type left with zero visible
  // entitlements while the flag is off doesn't misreport itself as "single".
  const singleEnt = entitlementsFor(form.type).length === 1;
  // Edit shows pricing read-only until the admin drafts a new version.
  const pricingReadOnly = isEdit && !pricingDraft;
  const activeVersion = isEdit
    ? ((prod.pricingHistory || []).find((v) => v.effectiveTo === null) || {}).version
    : null;
  // Until pricing is reached the preview shows the shape of the bill without the
  // figures — money is step 3's subject. Reopening an earlier step withdraws them
  // again, since they are about to be re-derived.
  const pricingReached = maxStep >= 3;
  // Type, billing period and fulfillment decide what gets provisioned, and the
  // priced rows are keyed by catalog key (which carries the billing period), so
  // changing them after creation would leave the product disagreeing with its own
  // prices. Fix a wrong one by archiving and creating another.
  const entitlementsLocked = isEdit;
  // Compare against the MIGRATED product: `prod` holds the raw stored fulfillment
  // (old shape for legacy products) while `form` holds the migrated one, so
  // without this a legacy product reads as dirty the instant it opens.
  const dirty = isEdit && editSnapshot(form) !== editSnapshot({ ...prod, fulfillment: migrateFulfillment(prod.fulfillment, prod.billing) });
  const currencySym = SYM[form.currency] || `${form.currency} `;
  const money = (n) => `${currencySym}${(Number(n) || 0).toLocaleString("en-IN")}`;
  // Placeholder bar as wide as the figure it stands in for, so filling it in at
  // step 3 doesn't reflow the panel. ~7.2px per character at this type size.
  const barWidth = (n) => Math.round(Math.max(44, money(n).length * 7.2));

  const lines = useMemo(
    () => buildLines({ form, sub, credits, creditFields: CF, catalog }),
    // form.currency belongs here: "was" prices are read from the catalog per
    // currency, so without it switching currency leaves stale originals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sub, credits, CF, form.subAmounts, form.subUnitPrices, form.creditPrices, form.originals, form.currency, catalog]
  );

  const base = sumLineItems(lines);

  // Seed each add-on's unit price from the catalog the first time it appears.
  // Fills blanks only, so it never overwrites a typed or loaded figure.
  useEffect(() => {
    if (pricingReadOnly) return;
    const addOns = (sub && sub.addOns) || [];
    if (!addOns.length) return;
    // Only the price is seeded; the "was" price is computed in the lines builder.
    const prices = {};
    addOns.forEach((a) => {
      const listed = catalogPriceOfKey(catalog, a.key, form.currency);
      if (listed == null) return;
      const cur = form.subUnitPrices[a.key];
      if (cur === undefined || cur === "") prices[a.key] = listed;
    });
    const planPatch = {};
    if (sub && sub.planKey) {
      const listedPlan = catalogPriceOfKey(catalog, sub.planKey, form.currency);
      if (listedPlan != null && !Number(form.subAmounts.plan)) {
        planPatch.subAmounts = { ...form.subAmounts, plan: listedPlan };
      }
    }
    if (Object.keys(prices).length || planPatch.subAmounts) {
      set({ ...planPatch, subUnitPrices: { ...form.subUnitPrices, ...prices } });
    }
  }, [sub && sub.addOns, form.currency, catalog, pricingReadOnly]);

  // ---- Validation ----
  const basicsErr = validateBasics(form);
  const pricingErr = validatePricing(
    { basePrice: base, minPrice: form.minPrice, currency: form.currency, migrationDeadline: form.migrationDeadline, lineItems: lines },
    // A new pricing version requires min < base; create allows min <= base.
    { requireStrictMin: isEdit && pricingDraft }
  );
  const shapeErr = validateFulfillmentShape(form.fulfillment);
  const fulfillmentPricingErr = validateFulfillmentAgainstPricing(form.fulfillment, lines, base);
  const subMissingPlan = !!sub && !sub.planKey;
  const negLine = lines.some((l) => Number(l.amount) < 0);
  const negFloor = Number(form.minPrice || 0) < 0;
  const negCredit = !!credits && CF.some((c) => Number(credits[c.k]) < 0);
  const noFulfillment = !form.fulfillment;
  const creditPriceMissing = !!credits && CF.some((c) => (Number(credits[c.k]) || 0) > 0 && !(Number(form.creditPrices[c.k]) > 0));

  const audienceErr =
    form.audience && form.audience.mode === "restricted" && !(form.audience.grants || []).length
      ? "Add at least one affiliate for a restricted product"
      : "";
  const step1Valid = !hasErrors(basicsErr) && !audienceErr;
  // Step 2 owns quantities only; credit price/unit is entered on the pricing row,
  // so it gates step 3 instead.
  const step2Valid = !hasErrors(shapeErr) && !subMissingPlan && !negCredit && !noFulfillment;
  const step3Valid = !hasErrors(pricingErr) && !fulfillmentPricingErr && !negLine && !negFloor && !creditPriceMissing;
  const allValid = step1Valid && step2Valid && step3Valid;

  // One source of truth for every error display below. The fallback keeps an
  // invalid step from going silent when it fails on a key with no message.
  const issuesByStep = (() => {
    const raw = buildStepIssues({
      basicsErr, shapeErr, pricingErr, fulfillmentPricingErr,
      subMissingPlan, noFulfillment, creditPriceMissing,
      negLine, negFloor, negCredit,
    });
    const valid = { 1: step1Valid, 2: step2Valid, 3: step3Valid };
    [1, 2, 3].forEach((n) => {
      if (!valid[n] && !raw[n].length) raw[n] = ["Fix the highlighted fields"];
    });
    return raw;
  })();

  // ---- Identity: features one-at-a-time ----
  const addFeature = () => {
    const v = featInput.trim();
    if (!v) return;
    set({ features: [...form.features, v] });
    setFeatInput("");
  };
  const removeFeature = (i) => set({ features: form.features.filter((_, idx) => idx !== i) });

  // ---- Entitlement toggles ----
  const patchFulfillment = (patch) => {
    const next = { ...(form.fulfillment || {}), ...patch };
    Object.keys(next).forEach((k) => { if (next[k] == null) delete next[k]; });
    set({ fulfillment: Object.keys(next).length ? next : null });
  };
  // Type drives fulfillment, so switching it starts from a clean slate.
  // Single-entitlement types auto-enable their one card; plan starts empty.
  const setType = (type) => setForm((f) => {
    if (type === f.type) return f;
    const visible = entitlementsFor(type);
    const only = visible.length === 1 ? visible[0] : null;
    let fulfillment = null;
    if (only === "webinar") fulfillment = { webinar: { meetingLink: "", emailSubject: "", whatsappTemplateName: "" } };
    else if (only === "credits") fulfillment = { credits: { ctwaAmount: "", wccAmount: 0 } };
    else if (only === "subscription") { const d = ["monthly","quarterly","yearly"].includes(f.billing?.period) ? f.billing.period : "monthly"; fulfillment = { subscription: { duration: d, planKey: null, addOns: [] } }; }
    // The blueprint is rebuilt from scratch, so every price and admin-entered
    // original keyed to the old one goes with it. Add-on keys are unknown here,
    // so the whole map is dropped rather than named ids.
    const originals = {};
    return {
      ...f, type, fulfillment,
      subAmounts: { plan: 0 },
      creditPrices: { ctwaAmount: "", wccAmount: "" },
      subUnitPrices: {},
      originals,
    };
  });
  // Per-unit prices and "was" prices live outside fulfillment.credits, so the
  // toggle has to clear them too or old prices reappear on the way back on.
  const toggleCredits = () => {
    const next = { ...(form.fulfillment || {}) };
    if (credits) delete next.credits;
    else next.credits = { ctwaAmount: "", wccAmount: "" };
    const originals = { ...form.originals };
    Object.values(CREDIT_LINE).forEach((id) => delete originals[id]);
    set({
      fulfillment: Object.keys(next).length ? next : null,
      creditPrices: { ctwaAmount: "", wccAmount: "" },
      originals,
    });
  };
  const toggleWebinar = () => patchFulfillment({ webinar: webinar ? null : { meetingLink: "", emailSubject: "", whatsappTemplateName: "" } });
  const patchSub = (patch) => patchFulfillment({ subscription: { ...sub, ...patch } });
  const patchCredits = (patch) => patchFulfillment({ credits: { ...credits, ...patch } });
  const setCreditPrice = (k, v) => set({ creditPrices: { ...form.creditPrices, [k]: v === "" ? "" : Number(v) } });
  const patchWebinar = (patch) => patchFulfillment({ webinar: { ...webinar, ...patch } });

  // The subscription's recurring duration IS the product's billing period.
  const subDuration = () =>
    (["monthly", "quarterly", "yearly"].includes(form.billing.period) ? form.billing.period : "monthly");

  // Starts with NO plan chosen: defaulting to whichever tier sorts first would
  // also seed that tier's price, quietly adding money to the total. The validator
  // already requires a plan or an add-on before the step passes.
  const toggleSub = () => {
    if (sub) { patchFulfillment({ subscription: null }); return; }
    patchFulfillment({ subscription: { duration: subDuration(), planKey: null, addOns: [] } });
  };

  // The plan tier currently selected (variant name), or "" for none.
  const currentPlanVariant = () => {
    if (!sub || !sub.planKey) return "";
    const meta = findByKey(catalog, sub.planKey);
    return meta ? meta.variant : "";
  };

  // Pick a plan tier (variant). "" clears the plan (add-ons-only bundle).
  const setPlanVariant = (variant) => {
    if (!variant) { patchSub({ planKey: null }); set({ subAmounts: { ...form.subAmounts, plan: 0 } }); return; }
    const key = keyFor(catalog, "plan", variant, sub.duration);
    patchSub({ planKey: key });
    // The plan row is keyed "plan", not by catalog key, so nothing re-seeds it and
    // BASIC's price would silently follow you to PRO. Reset it explicitly.
    const listed = catalogPriceOfKey(catalog, key, form.currency);
    set({ subAmounts: { ...form.subAmounts, plan: listed == null ? 0 : listed } });
  };

  // The add-on entry for a family, or undefined.
  const addonOf = (family) => ((sub && sub.addOns) || []).find((a) => a.family === family);

  const addAddon = (family) => {
    const vs = variantsAtDuration(catalog, family, sub.duration);
    if (!vs.length) { showSnackbar(`No ${familyLabel(catalog, family)} options for ${sub.duration}`, "error"); return; }
    // Flows must start at the BASE pack, not whichever variant sorts first: the
    // ladder is base + k increments, so the base is always present.
    if (family === "flow_addon") {
      const packs = flowPacksAt(catalog, sub.duration);
      if (!packs) { showSnackbar(`No flow packs for ${sub.duration}`, "error"); return; }
      patchSub({ addOns: [...(sub.addOns || []), ...flowAddOnsFor(packs, 0)] });
      return;
    }
    const key = vs[0].durations[sub.duration].key;
    patchSub({ addOns: [...(sub.addOns || []), { family, key, quantity: 1 }] });
  };

  // Change which variant of a family is selected.
  const setAddonVariant = (family, variant) => {
    const key = keyFor(catalog, family, variant, sub.duration);
    patchSub({ addOns: (sub.addOns || []).map((a) => (a.family === family ? { ...a, key } : a)) });
  };

  // Team-members seat count.
  const setAddonQty = (family, qty) => {
    const n = qty === "" ? "" : Math.max(1, Math.floor(Number(qty) || 0));
    patchSub({ addOns: (sub.addOns || []).map((a) => (a.family === family ? { ...a, quantity: n } : a)) });
  };

  // Drops every entry the family had along with its prices, so switching it back
  // on starts clean instead of resurrecting numbers for a row that's gone.
  const removeAddon = (family) => {
    const dropped = (sub.addOns || []).filter((a) => a.family === family).map((a) => a.key);
    const nextPrices = { ...form.subUnitPrices };
    const nextOriginals = { ...form.originals };
    dropped.forEach((k) => { delete nextPrices[k]; delete nextOriginals[k]; });
    patchSub({ addOns: (sub.addOns || []).filter((a) => a.family !== family) });
    set({ subUnitPrices: nextPrices, originals: nextOriginals });
  };

  const setSubUnitPrice = (k, v) => set({ subUnitPrices: { ...form.subUnitPrices, [k]: v === "" ? "" : Number(v) } });

  // True if any subscription price differs from the catalog rate for the CURRENT
  // term — i.e. the admin typed something a period change is about to discard.
  const hasCustomSubPrice = () => {
    if (!sub) return false;
    const differs = (val, key) => {
      const listed = catalogPriceOfKey(catalog, key, form.currency);
      return listed != null && Number(val) > 0 && Math.abs(Number(val) - listed) > 0.005;
    };
    if (sub.planKey && differs(form.subAmounts.plan, sub.planKey)) return true;
    return (sub.addOns || []).some((a) => differs(form.subUnitPrices[a.key], a.key));
  };

  const setBillingPeriod = (period) => {
    // Warn outside the updater: a snackbar fired inside setForm would run during
    // the render phase and could fire twice.
    if (period !== form.billing.period && hasCustomSubPrice()) {
      showSnackbar(`Prices reset to the ${period} catalog rates`, "info");
    }
    setBillingPeriodState(period);
  };

  const setBillingPeriodState = (period) => setForm((f) => {
    const next = { ...f, billing: { period, label: billingLabelFor(period) } };
    const s = f.fulfillment && f.fulfillment.subscription;
    if (s && ["monthly", "quarterly", "yearly"].includes(period)) {
      const reKey = (key) => {
        const m = findByKey(catalog, key);
        return m ? (keyFor(catalog, m.family, m.variant, period) || key) : key;
      };
      const nextAddOns = (s.addOns || []).map((a) => ({ ...a, key: reKey(a.key) }));
      next.fulfillment = {
        ...f.fulfillment,
        subscription: {
          ...s,
          duration: period,
          planKey: s.planKey ? reKey(s.planKey) : null,
          addOns: nextAddOns,
        },
      };
      // Every catalog key just moved to a different term, and a monthly pack does
      // not cost what a yearly one does. Blanking the prices lets the seeding
      // effect (which only fills BLANKS) re-read the new term's rates. "Was"
      // prices follow the keys on their own; credit prices aren't term-dependent.
      next.subUnitPrices = {};
      next.subAmounts = { ...f.subAmounts, plan: 0 };
    }
    return next;
  });

  const setSubAmount = (id, v) => set({ subAmounts: { ...form.subAmounts, [id]: v === "" ? "" : Number(v) } });

  // ---- Step navigation ----
  // Reopening a completed step re-locks everything after it, so Continue stays the
  // single commit point where validation runs and pricing re-reads entitlements.
  const openStep = (n) => {
    if (n > maxStep) return;
    setActiveStep(n);
    if (!isEdit && n < maxStep) setMaxStep(n);
  };
  const continueFrom1 = () => {
    setAttempted((a) => ({ ...a, 1: true }));
    if (!step1Valid) { showSnackbar(issueSummary(issuesByStep[1]), "error"); return; }
    setMaxStep((m) => Math.max(m, 2)); setActiveStep(2);
  };
  const continueFrom2 = () => {
    setAttempted((a) => ({ ...a, 2: true }));
    if (!step2Valid) { showSnackbar(issueSummary(issuesByStep[2]), "error"); return; }
    setMaxStep((m) => Math.max(m, 3)); setActiveStep(3);
  };

  const close = () => onClose();

  // ---- Saving ----
  // Credit amounts are held as strings while editing (so the field can be
  // emptied); coerce to numbers before sending.
  const buildFulfillment = () => {
    if (!form.fulfillment) return form.fulfillment;
    const ff = { ...form.fulfillment };
    if (ff.subscription) {
      ff.subscription = {
        duration: ff.subscription.duration,
        planKey: ff.subscription.planKey || null,
        addOns: (ff.subscription.addOns || []).map((a) => ({
          family: a.family, key: a.key, quantity: Number(a.quantity) || 1,
        })),
      };
    }
    if (ff.credits) {
      ff.credits = {
        ctwaAmount: Number(ff.credits.ctwaAmount) || 0,
        wccAmount: Number(ff.credits.wccAmount) || 0,
      };
    }
    return ff;
  };

  const outAudience = () =>
    form.audience && form.audience.mode === "restricted"
      ? { mode: "restricted", grants: form.audience.grants }
      : { mode: "everyone", grants: [] };

  const reportIssues = (steps) => {
    const bad = steps.filter((n) => issuesByStep[n].length);
    const total = bad.reduce((t, n) => t + issuesByStep[n].length, 0);
    showSnackbar(total === 1 ? issuesByStep[bad[0]][0] : `${issueCount(total)} in ${joinStepNames(bad)}`, "error");
  };

  // Edit saves stay on the screen, so refresh the local product (for the pricing
  // history) and the list in the background. The form already matches.
  const refreshProduct = async () => {
    const fresh = await getProduct(prod._id);
    if (fresh) setProd(fresh);
    onSaved();
  };

  // Edit: save identity + fulfillment (pricing is a separate versioned action).
  const saveEdit = async () => {
    if (!step1Valid || !step2Valid) { reportIssues([1, 2]); return; }
    setSaving(true);
    try {
      await updateProduct(prod._id, {
        name: form.name.trim(), type: form.type, status: form.status,
        billing: { period: form.billing.period, label: form.billing.label.trim() },
        planTitle: form.planTitle.trim(), planSubtitle: form.planSubtitle.trim(),
        features: form.features, fulfillment: buildFulfillment(),
        audience: outAudience(),
      });
      await refreshProduct();
      showSnackbar("Changes saved", "success");
    } catch (err) {
      const msg = apiErrorMessage(err);
      if (/already exists/i.test(msg)) { setNameError(msg); openStep(1); }
      showSnackbar(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  // Edit: publish the drafted pricing as a new version, staying on the screen.
  const publishVersion = async () => {
    if (!step3Valid) { reportIssues([3]); return; }
    setSaving(true);
    try {
      await addPricingVersion(prod._id, {
        basePrice: base, minPrice: Number(form.minPrice), currency: form.currency,
        migrationDeadline: form.migrationDeadline || null, lineItems: toLineItems(lines),
      });
      setPricingDraft(false);
      await refreshProduct();
      showSnackbar("New pricing version published", "success");
    } catch (err) {
      showSnackbar(apiErrorMessage(err), "error");
    } finally {
      setSaving(false);
    }
  };

  // Discard a pricing draft, restoring the product's active version.
  const cancelDraft = () => {
    const f = formFromProduct(prod);
    set({
      subAmounts: f.subAmounts, creditPrices: f.creditPrices, subUnitPrices: f.subUnitPrices,
      originals: f.originals, minPrice: f.minPrice, currency: f.currency, migrationDeadline: "",
    });
    setPricingDraft(false);
  };

  const submit = async () => {
    if (isEdit) return saveEdit();
    setAttempted({ 1: true, 2: true, 3: true });
    if (!allValid) { reportIssues([1, 2, 3]); return; }
    setSaving(true);
    try {
      await createProduct({
        name: form.name.trim(),
        type: form.type,
        status: "active",
        billing: { period: form.billing.period, label: form.billing.label.trim() },
        planTitle: form.planTitle.trim(),
        planSubtitle: form.planSubtitle.trim(),
        features: form.features,
        fulfillment: buildFulfillment(),
        audience: outAudience(),
        initialPricing: {
          basePrice: base,
          minPrice: Number(form.minPrice),
          currency: form.currency,
          migrationDeadline: form.migrationDeadline || null,
          lineItems: toLineItems(lines),
        },
      });
      showSnackbar("Product created", "success");
      setSaving(false);
      onSaved();
      close();
    } catch (err) {
      const msg = apiErrorMessage(err);
      if (/already exists/i.test(msg)) { setNameError(msg); openStep(1); }
      showSnackbar(msg, "error");
      setSaving(false);
    }
  };

  const lockMark = () => (entitlementsLocked
    ? <LockOutlinedIcon className={classes.lockIco} titleAccess="Fixed after creation" />
    : null);

  const createTooltip = () => {
    if (maxStep < 3) return "Complete the steps to create";
    if (allValid) return "";
    return (
      <Box>
        {[1, 2, 3].filter((n) => issuesByStep[n].length).map((n) => (
          <Box key={n} mb={0.5}>
            <div style={{ fontWeight: 700 }}>{STEP_NAMES[n]}</div>
            {issuesByStep[n].map((m, i) => <div key={i}>• {m}</div>)}
          </Box>
        ))}
      </Box>
    );
  };

  // Collapsed-section summaries.
  const entSummary = (() => {
    const parts = [];
    if (sub) parts.push("Subscription");
    if (credits) parts.push("Credits");
    if (webinar) parts.push("Webinar");
    return parts.length ? parts.join(" · ") : "No entitlements";
  })();

  // Shared by all three cards; only n/title/summary/badge/children differ.
  const sectionProps = {
    activeStep, maxStep, attempted, isEdit, onOpen: openStep, classes,
  };

  return (
    <Box className={classes.page}>
      <Box className={classes.topbar}>
        <Box>
          <button type="button" className={classes.back} onClick={close} disabled={saving}>
            <ArrowBackIcon style={{ fontSize: 13 }} /> Checkout Products
          </button>
          <div className={classes.topTitle}>{isEdit ? `Edit product · ${prod.name}` : "New product"}</div>
        </Box>
        <Box className={classes.pills}>
          {isEdit ? (
            // Type is fixed after creation: show it, don't let it change.
            <Tooltip title="Type can't be changed after creation">
              <span className={`${classes.pill} ${classes.pillOn}`}>
                {(TYPE_PILLS.find((t) => t.value === form.type) || {}).label || form.type}
              </span>
            </Tooltip>
          ) : (
            VISIBLE_TYPE_PILLS.map((t) => t.disabled ? (
              <Tooltip key={t.value} title="Coming soon">
                <span className={`${classes.pill} ${classes.pillDisabled}`}>{t.label}</span>
              </Tooltip>
            ) : (
              <div key={t.value} className={`${classes.pill} ${form.type === t.value ? classes.pillOn : ""}`}
                onClick={() => setType(t.value)}>{t.label}</div>
            ))
          )}
        </Box>
        <Box display="flex" style={{ gap: 8 }}>
          <Button onClick={close} disabled={saving}>Cancel</Button>
          <Tooltip title={isEdit ? (dirty ? "" : "No changes to save") : createTooltip()}>
            <span>
              <Button className={classes.createBtn} variant="contained" onClick={submit}
                disabled={saving || (isEdit ? !dirty : (maxStep < 3 || !allValid))}>
                {isEdit ? "Save changes" : "Create product"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box className={classes.wrap}>
        <Box className={classes.builder}>
          <StepSection {...sectionProps} n={1}
            title="Identity" subTitle="what the buyer sees"
            summary={form.name ? `${form.name} · ${form.type}` : "—"}
            issues={issuesByStep[1]}>
            <IdentityStep
              classes={classes} form={form} set={set} isEdit={isEdit}
              showErrors={attempted[1]} basicsErr={basicsErr}
              nameError={nameError} setNameError={setNameError}
              billingLocked={entitlementsLocked} lockMark={lockMark} onBillingPeriod={setBillingPeriod}
              featInput={featInput} setFeatInput={setFeatInput}
              onAddFeature={addFeature} onRemoveFeature={removeFeature}
              audienceLabels={audienceLabels} setAudienceLabels={setAudienceLabels}
              issues={attempted[1] ? issuesByStep[1] : []} onContinue={continueFrom1} />
          </StepSection>

          <StepSection {...sectionProps} n={2}
            title="What the customer gets" subTitle="delivered after purchase"
            summary={entSummary}
            issues={issuesByStep[2]}
            badge={entitlementsLocked ? (
              <span className={classes.secBadge}><LockOutlinedIcon style={{ fontSize: 11 }} /> Fixed</span>
            ) : null}>
            <EntitlementsStep
              classes={classes} catalog={catalog} form={form}
              sub={sub} credits={credits} webinar={webinar} creditFields={CF}
              singleEnt={singleEnt} locked={entitlementsLocked} lockMark={lockMark}
              showErrors={attempted[2]} shapeErr={shapeErr}
              onToggleSub={toggleSub} onToggleCredits={toggleCredits} onToggleWebinar={toggleWebinar}
              planVariant={currentPlanVariant()} onSetPlanVariant={setPlanVariant}
              addonOf={addonOf} onAddAddon={addAddon} onRemoveAddon={removeAddon}
              onSetAddonVariant={setAddonVariant} onSetAddonQty={setAddonQty} onPatchSub={patchSub}
              onPatchCredits={patchCredits} onPatchWebinar={patchWebinar}
              issues={attempted[2] ? issuesByStep[2] : []}
              onBack={() => openStep(1)} onContinue={continueFrom2} />
          </StepSection>

          <StepSection {...sectionProps} n={3}
            title="Pricing" subTitle="one line per thing you're selling"
            summary={money(base)}
            issues={issuesByStep[3]}
            badge={pricingReadOnly ? (
              <span className={classes.secBadge}>
                <LockOutlinedIcon style={{ fontSize: 11 }} /> Version {activeVersion}
              </span>
            ) : null}>
            <PricingStep
              classes={classes} form={form} set={set} product={prod}
              isEdit={isEdit} readOnly={pricingReadOnly} pricingDraft={pricingDraft}
              activeVersion={activeVersion} lines={lines} base={base}
              currencySym={currencySym} money={money}
              showErrors={attempted[3]} pricingErr={pricingErr} negFloor={negFloor} saving={saving}
              onCreditPrice={setCreditPrice} onSubUnitPrice={setSubUnitPrice} onSubAmount={setSubAmount}
              issues={attempted[3] ? issuesByStep[3] : []}
              onBack={() => openStep(2)} onStartDraft={() => setPricingDraft(true)}
              onCancelDraft={cancelDraft} onPublish={publishVersion} />
          </StepSection>
        </Box>

        <BuyerPreview
          classes={classes} form={form} set={set}
          sub={sub} credits={credits} webinar={webinar} creditFields={CF} catalog={catalog}
          lines={lines} base={base} pricingReached={pricingReached}
          money={money} barWidth={barWidth} />
      </Box>
    </Box>
  );
};

export default ProductStudio;
